import jsPDF from "jspdf";

// jsPDF's default Helvetica font can't render the ₹ (U+20B9) glyph — it shows as "1".
// So we output "Rs." everywhere for reliable rendering.
const RS = "Rs.";

function money(v) {
  return `${RS} ${Number(v || 0).toLocaleString("en-IN")}`;
}

export function generateReceiptPdf(receipt) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const t = receipt.template_snapshot || {};
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 40;
  let y = 50;

  const line = (x1, y1, x2, y2) => doc.line(x1, y1, x2, y2);
  const setFont = (size, style = "normal") => {
    doc.setFont("helvetica", style);
    doc.setFontSize(size);
  };

  // --- Header ---
  setFont(22, "bold");
  doc.text(t.hostel_name || "Vasavi Boys Hostel", pageW / 2, y, { align: "center" });
  y += 18;

  // Tagline sits immediately BELOW the hostel name (user requested this order)
  if (t.header_note) {
    setFont(10, "italic");
    doc.text(t.header_note, pageW / 2, y, { align: "center" });
    y += 14;
  }

  setFont(9);
  if (t.address_line_1) {
    doc.text(t.address_line_1, pageW / 2, y, { align: "center" });
    y += 12;
  }
  if (t.address_line_2) {
    const wrapped = doc.splitTextToSize(t.address_line_2, pageW - margin * 2);
    doc.text(wrapped, pageW / 2, y, { align: "center" });
    y += wrapped.length * 11;
  }
  if (t.gst_pan) {
    setFont(9, "bold");
    doc.text(`LIN NO: ${t.gst_pan}`, pageW / 2, y + 4, { align: "center" });
    y += 14;
  }
  y += 6;
  doc.setLineWidth(1.2);
  line(margin, y, pageW - margin, y);
  y += 22;

  // --- Title ---
  setFont(16, "bold");
  doc.text("PAYMENT RECEIPT", pageW / 2, y, { align: "center" });
  y += 22;

  // --- Meta ---
  setFont(10);
  const paymentDate = new Date(receipt.payment_date || receipt.issued_at).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  doc.text(`Receipt No: ${receipt.receipt_number}`, margin, y);
  doc.text(`Date: ${paymentDate}`, pageW - margin, y, { align: "right" });
  y += 22;

  // --- Student box ---
  doc.setDrawColor(10);
  doc.setLineWidth(0.8);
  doc.rect(margin, y - 12, pageW - margin * 2, 78);
  setFont(9, "bold");
  const col1 = margin + 10;
  const col2 = pageW / 2 + 10;
  doc.text("Received From", col1, y);
  doc.text("Room", col2, y);
  setFont(11, "bold");
  doc.text(receipt.student_name || "-", col1, y + 14);
  doc.text(receipt.flat_number || "-", col2, y + 14);
  setFont(9, "bold");
  doc.text("Student ID", col1, y + 32);
  doc.text("KL ID", col2, y + 32);
  setFont(11);
  doc.text(receipt.student_id_number || "-", col1, y + 46);
  doc.text(receipt.kl_id || "-", col2, y + 46);
  y += 88;

  // --- Amount box (highlighted) — dynamic height so long Ref text wraps
  //     inside the left column without colliding with the right-aligned amount
  const amtLabelX = margin + 12;
  const amtValueRightX = pageW - margin - 14;
  // Reserve ~170pt on the right for the amount value; wrap Ref within the rest
  const refMaxWidth = amtValueRightX - amtLabelX - 170;

  setFont(9);
  const refLines = receipt.payment_reference
    ? doc.splitTextToSize(`Ref: ${receipt.payment_reference}`, refMaxWidth)
    : [];
  const amtBoxH = refLines.length > 1 ? 46 + refLines.length * 11 + 6 : 62;

  doc.setFillColor(255, 230, 0);
  doc.rect(margin, y, pageW - margin * 2, amtBoxH, "FD");

  setFont(9, "bold");
  doc.text("AMOUNT RECEIVED", amtLabelX, y + 18);
  setFont(9);
  doc.text(`Payment Method: ${receipt.payment_method || "Cash"}`, amtLabelX, y + 34);
  if (refLines.length) {
    doc.text(refLines, amtLabelX, y + 46);
  }

  // Amount — right-aligned, vertically centered in the box
  setFont(18, "bold");
  const amtText = money(receipt.amount);
  doc.text(amtText, amtValueRightX, y + amtBoxH / 2, { align: "right" });

  y += amtBoxH + 16;

  // --- Ledger summary ---
  setFont(10, "bold");
  doc.text("Ledger Summary", margin, y);
  y += 8;
  const rows = [
    ["Total Fees", money(receipt.total_fees)],
    ["Discount", money(receipt.discount_amount)],
    ["Paid To Date", money(receipt.fees_paid_after)],
    ["Balance Due", money(receipt.balance_due)],
  ];
  setFont(10);
  rows.forEach(([label, val], i) => {
    const rowY = y + 14 + i * 18;
    doc.setFillColor(i % 2 === 0 ? 245 : 255, i % 2 === 0 ? 245 : 255, i % 2 === 0 ? 245 : 255);
    doc.rect(margin, rowY - 12, pageW - margin * 2, 18, "F");
    doc.text(label, margin + 10, rowY);
    doc.text(val, pageW - margin - 10, rowY, { align: "right" });
  });
  y += 14 + rows.length * 18 + 10;

  if (receipt.notes) {
    setFont(9, "italic");
    const notesLines = doc.splitTextToSize(`Notes: ${receipt.notes}`, pageW - margin * 2);
    doc.text(notesLines, margin, y);
    y += notesLines.length * 12 + 4;
  }
  if (t.terms_line) {
    setFont(8, "italic");
    doc.setTextColor(90);
    const terms = doc.splitTextToSize(t.terms_line, pageW - margin * 2);
    doc.text(terms, margin, y);
    doc.setTextColor(0);
    y += terms.length * 10 + 6;
  }

  // --- Signature ---
  y = Math.max(y, pageH - 130);
  setFont(9);
  doc.text(`Issued by: ${receipt.issued_by || "Admin"}`, margin, y);
  setFont(10, "bold");
  doc.text(t.signatory_name || "Warden", pageW - margin, y + 24, { align: "right" });
  setFont(9);
  doc.text(t.signatory_role || "Authorized Signatory", pageW - margin, y + 38, { align: "right" });

  // --- Footer ---
  if (t.footer_note) {
    setFont(8, "italic");
    doc.setTextColor(120);
    const foot = doc.splitTextToSize(t.footer_note, pageW - margin * 2);
    doc.text(foot, pageW / 2, pageH - 30, { align: "center" });
    doc.setTextColor(0);
  }

  const filename = `Receipt-${(receipt.receipt_number || "receipt").replaceAll("/", "-")}.pdf`;
  doc.save(filename);
}
